import React, { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";

/* Numbered manifesto chapters — editorial rhythm, big display type */
const CHAPTERS = [
  {
    n: "01",
    kicker: "Origem",
    heading: "Nascemos em 2006 para transformar risco em controle operacional.",
    body:
      "A HSE Soluções e Serviços foi fundada com um propósito claro — gerar valor onde qualquer erro tem custo humano. Segurança do trabalho, saúde ocupacional, meio ambiente e qualidade deixaram de ser exigência para virar método. Vinte anos depois, esse método é a nossa engenharia.",
  },
  {
    n: "02",
    kicker: "Capacidade",
    heading: "Uma equipe técnica multidisciplinar que executa o que projeta.",
    body:
      "Engenheiros, técnicos de segurança, bombeiros civis, resgatistas, especialistas em altura, espaço confinado e alpinismo industrial. Uma só operação, integrada em campo, com equipamento certificado e disciplina de procedimento. Da análise de risco à execução final.",
  },
  {
    n: "03",
    kicker: "Compromisso",
    heading: "Somos uma empresa de segurança apta à realização de serviços críticos.",
    body:
      "Trabalhamos ao lado de indústrias que não podem parar. Paradas de manutenção, plantas em operação, ativos em altura, ambientes confinados, resposta a emergências. Onde o serviço é crítico, a segurança precisa ser especializada — e é isso que a HSE entrega há duas décadas.",
  },
];

const ChapterRow = ({ ch, i }) => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start 85%", "end 15%"] });
  const y = useTransform(scrollYProgress, [0, 1], [40, -40]);

  return (
    <motion.article
      ref={ref}
      data-testid={`manifesto-chapter-${ch.n}`}
      initial={{ opacity: 0 }}
      whileInView={{ opacity: 1 }}
      viewport={{ once: true, margin: "-80px" }}
      transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
      className={`grid grid-cols-12 gap-6 sm:gap-10 py-16 sm:py-24 border-b border-white/10 ${
        i === 0 ? "border-t" : ""
      }`}
    >
      {/* Number */}
      <motion.div style={{ y }} className="col-span-12 sm:col-span-3 lg:col-span-2">
        <div className="sticky top-28">
          <div className="font-condensed font-bold text-[#2FA36B] tracking-[-0.04em] leading-none text-[6rem] sm:text-[7rem] lg:text-[9rem]">
            {ch.n}
          </div>
          <div className="mt-2 font-condensed uppercase text-[11px] tracking-[0.35em] text-white/50 font-semibold">
            {ch.kicker}
          </div>
        </div>
      </motion.div>

      {/* Text */}
      <div className="col-span-12 sm:col-span-9 lg:col-span-9 lg:col-start-4">
        <motion.h3
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1], delay: 0.1 }}
          className="font-condensed font-bold uppercase text-white leading-[0.95] tracking-[-0.015em] text-3xl sm:text-5xl lg:text-6xl"
        >
          {ch.heading}
        </motion.h3>
        <motion.p
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1], delay: 0.25 }}
          className="mt-6 max-w-2xl text-white/70 font-body text-base sm:text-lg leading-relaxed"
        >
          {ch.body}
        </motion.p>
      </div>
    </motion.article>
  );
};

export const About = () => {
  return (
    <section id="sobre" data-testid="section-sobre" className="relative py-24 sm:py-36 bg-[#05121F]">
      <div className="max-w-[1400px] mx-auto px-5 sm:px-8">
        {/* Section intro */}
        <div className="grid lg:grid-cols-12 gap-10 items-end mb-12 sm:mb-16">
          <div className="lg:col-span-8">
            <div className="inline-flex items-center gap-3 mb-6">
              <span className="w-8 h-px bg-[#2FA36B]" />
              <span className="font-condensed uppercase text-xs tracking-[0.4em] text-[#2FA36B] font-bold">
                Manifesto · Sobre a HSE
              </span>
            </div>
            <h2 className="font-condensed font-bold uppercase text-white leading-[0.92] tracking-[-0.02em] text-4xl sm:text-6xl lg:text-7xl">
              Engenharia de segurança para
              <span className="text-[#2FA36B]"> serviços críticos</span>.
            </h2>
          </div>
          <div className="lg:col-span-4">
            <p className="text-white/60 font-body text-sm leading-relaxed">
              Três capítulos que resumem duas décadas de operação. Origem, capacidade e compromisso — os pilares que sustentam cada projeto entregue pela HSE.
            </p>
          </div>
        </div>

        {/* Chapters */}
        <div>
          {CHAPTERS.map((ch, i) => (
            <ChapterRow key={ch.n} ch={ch} i={i} />
          ))}
        </div>
      </div>
    </section>
  );
};

export default About;
